import React from 'react';
import { PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';
import { ChartConfig, ChartContainer, ChartTooltip } from '@frontend/shadcn/components/ui/chart';
import { formatCurrency } from '../../../utils/formatCurrency';

type MetricRadialChartProps = {
  aspireValue: number;
  nonAspireValue: number;
  metricType: 'currency' | 'percentage';
  metricName: string;
  currency?: string;
};

export const MetricRadialChart = ({
  aspireValue,
  nonAspireValue,
  metricType,
  metricName,
  currency = 'USD',
}: MetricRadialChartProps) => {
  const chartData = [
    {
      name: metricName,
      aspire: aspireValue,
      nonAspire: nonAspireValue,
    },
  ];

  const chartConfig: ChartConfig = {
    aspire: {
      label: 'Attributed to Aspire',
      color: 'hsl(var(--refresh-blue-5))', // Dark blue
    },
    nonAspire: {
      label: 'Not Attributed to Aspire',
      color: 'hsl(var(--refresh-blue-2))', // Light blue
    },
  };

  const formatValue = (value: number) => {
    if (metricType === 'currency') {
      return formatCurrency(value, currency);
    } else {
      return `${value.toFixed(1)}%`;
    }
  };

  const CustomTooltipContent = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-background border border-border rounded-lg shadow-lg p-2">
          {payload.map((entry: any, index: number) => (
            <div key={index} className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: entry.color }} />
              <span className="text-sm">
                {entry.dataKey === 'aspire' ? 'Attributed to Aspire' : 'Not Attributed to Aspire'}:{' '}
                {formatValue(entry.value)}
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full">
      <ChartContainer config={chartConfig} className="mx-auto h-full w-full max-w-[250px]">
        <RadialBarChart
          data={chartData}
          startAngle={180}
          endAngle={0}
          innerRadius="60%"
          outerRadius="120%"
          margin={{ top: 0, left: 0, right: 0, bottom: 0 }}
        >
          <ChartTooltip cursor={false} content={<CustomTooltipContent />} />
          <PolarRadiusAxis tick={false} tickLine={false} axisLine={false} />
          <RadialBar
            dataKey="aspire"
            stackId="a"
            cornerRadius={4}
            fill="var(--color-aspire)"
            className="stroke-transparent stroke-2"
          />
          <RadialBar
            dataKey="nonAspire"
            fill="var(--color-nonAspire)"
            stackId="a"
            cornerRadius={4}
            className="stroke-transparent stroke-2"
          />
        </RadialBarChart>
      </ChartContainer>
    </div>
  );
};
